import type {
  AgeGroupEnum,
  BikeLevelEnum,
  BundleTravelStatusEnum,
  CardDeviceEnum,
  CardStatusEnum,
  ChallengeGoalTypeEnum,
  CheckoutCodeStatusEnum,
  CouponTypeEnum,
  CurrencyEnum,
  DealerOrderStatusEnum,
  DeviceEnum,
  DiscountTypeEnum,
  DiscountVarietyEnum,
  EquipmentLevelEnum,
  EquipmentTypeEnum,
  ExtendedCancelationRefundEnum,
  ExternalCodeProviderNameEnum,
  ExternalCodeStatusEnum,
  ExternalCodeValueTypeEnum,
  ExternalProviderParamsNode,
  FormStatusEnum,
  GenderEnum,
  LessonTypeEnum,
  MarkupTypeEnum,
  MembershipCardAssignmentStatusEnum,
  MembershipIntervalEnum,
  MembershipStatusEnum,
  MembershipTransitStatusEnum,
  MembershipTypeEnum,
  MissingStepsOptionsEnum,
  OrderStatusEnum,
  PartnerBusinessTypeEnum,
  PartnerTypeEnum,
  PaymentBrandEnum,
  PaymentGatewayEnum,
  PaymentTypeEnum,
  PolicyTypeEnum,
  PpuProviderNameEnum,
  PriceRuleEnum,
  ProductBarcodeModeEnum,
  ProductTypeEnum,
  ProfileLevelEnum,
  ProfileStatusEnum,
  ProfileTypeEnum,
  ProviderOrderStatusEnum,
  ProviderOrderStatusSemaphoreEnum,
  ShippingProviderEnum,
  SnowitcardsTypesEnum,
  SocialPostTypeEnum,
  TaxonomyTypeEnum,
  TenantAnalyticsEnum,
  TenantEmailTypeEnum,
  TenantFooterMenuEntriesEnum,
  TenantLoginEnum,
  TenantMenuEntriesEnum,
  TenantScriptsEnum,
  TenantWidgetComponentEnum,
  TenantWidgetFilterEnum,
  TenantWidgetValidatorEnum,
  TurnstileDirectionEnum,
  TurnstileTypeEnum,
  UserSubTypeEnum,
  UserTypeEnum,
  ValidLocaleEnum,
  VenueTypeEnum,
  VideoTypeEnum,
  WalletSeasonStatusEnum,
  ZoneDirectionTypeEnum,
  ZonePPUClassificationEnum,
  ZoneServiceEnum,
  ZoneTextBlockTypeEnum,
} from '@snowit/models';
import type { ApiKeyServices } from '@snowit/types/apikeys';

type Dict<K extends string> = Record<K, K>;

const TRANSIT_STATUS_ENUM: Dict<MembershipTransitStatusEnum> = {
  pending: 'pending',
  closed: 'closed',
  discarded: 'discarded',
};

const TURNSTILE_TYPE_ENUM: Dict<TurnstileTypeEnum> = {
  cable_car: 'cable_car',
  chair_lift: 'chair_lift',
  drag_lift: 'drag_lift',
  gondola: 'gondola',
  funicular: 'funicular',
  't-bar': 't-bar',
  mixed_lift: 'mixed_lift',
  magic_carpet: 'magic_carpet',
  platter: 'platter',
  rope_tow: 'rope_tow',
  railway: 'railway',
  'j-bar': 'j-bar',
};

const TURNSTILE_DIRECTION_ENUM: Dict<TurnstileDirectionEnum> = {
  uphill: 'uphill',
  downhill: 'downhill',
};

const CARD_DEVICE_ENUM: Dict<CardDeviceEnum> = {
  card: 'card',
  bracelet: 'bracelet',
};

const CHALLENGE_GOAL_TYPE_ENUM: Dict<ChallengeGoalTypeEnum> = {
  dayCount: 'dayCount',
  distance: 'distance',
  district: 'district',
  heightDifference: 'heightDifference',
  districtsCount: 'districtsCount',
  maxAltitude: 'maxAltitude',
  skiTime: 'skiTime',
  turnstilesCount: 'turnstilesCount',
};

const EXTERNAL_PROVIDER_PARAMS_KEYS_ENUM: Dict<keyof ExternalProviderParamsNode> = {
  alfi: 'alfi',
  anef: 'anef',
  axepta: 'axepta',
  axess: 'axess',
  beebeeboard: 'beebeeboard',
  beneficy: 'beneficy',
  bitq: 'bitq',
  brevo: 'brevo',
  cervinia: 'cervinia',
  comunemilano: 'comunemilano',
  coupon: 'coupon',
  discount: 'discount',
  easyrent: 'easyrent',
  esselunga: 'esselunga',
  evai: 'evai',
  gardaticket: 'gardaticket',
  getyourguide: 'getyourguide',
  google: 'google',
  improoving: 'improoving',
  jointly: 'jointly',
  kappadue: 'kappadue',
  midaticket: 'midaticket',
  netstorming: 'netstorming',
  none: 'none',
  offline: 'offline',
  'ppu~seasonal': 'ppu~seasonal',
  pricenow: 'pricenow',
  'promo-cf-lombardia': 'promo-cf-lombardia',
  'promo-milano-cortina': 'promo-milano-cortina',
  quickbooks: 'quickbooks',
  satispay: 'satispay',
  scalapay: 'scalapay',
  sfmc: 'sfmc',
  shippyPro: 'shippyPro',
  spotlio: 'spotlio',
  skidata: 'skidata',
  skiperformance: 'skiperformance',
  skiwork: 'skiwork',
  stripe: 'stripe',
  tantosvago: 'tantosvago',
  trenitalia: 'trenitalia',
  'trentino-marketing': 'trentino-marketing',
  'trenord-loyalty': 'trenord-loyalty',
  trenord: 'trenord',
  zucchetti: 'zucchetti',
};

const PPU_PROVIDERS_ENUM: Dict<PpuProviderNameEnum> = {
  anef: 'anef',
  axess: 'axess',
  skidata: 'skidata',
};

const PRODUCT_TYPES_ENUM: Record<ProductTypeEnum, ProductTypeEnum> = {
  bundle: 'bundle',
  'bundle~dynamic': 'bundle~dynamic',
  'bundle~family-pack': 'bundle~family-pack',
  'bundle~tab': 'bundle~tab',
  'bundle~train': 'bundle~train',
  'bundle~travel': 'bundle~travel',
  coupon: 'coupon',
  'coupon~giftcard': 'coupon~giftcard',
  experience: 'experience',
  'experience~museum': 'experience~museum',
  food: 'food',
  giftcard: 'giftcard',
  hotel: 'hotel',
  'hotel~coupon': 'hotel~coupon',
  insurance: 'insurance',
  'insurance~travel': 'insurance~travel',
  itinerary: 'itinerary',
  lesson: 'lesson',
  'lesson~hotel': 'lesson~hotel',
  matrioska: 'matrioska',
  merchandise: 'merchandise',
  parking: 'parking',
  rental: 'rental',
  'rental~bike': 'rental~bike',
  'rental~hotel': 'rental~hotel',
  seasonal: 'seasonal',
  service: 'service',
  'service~free': 'service~free',
  skipass: 'skipass',
  'skipass~dynamic': 'skipass~dynamic',
  'skipass~hotel': 'skipass~hotel',
  'skipass~membership-seasonal': 'skipass~membership-seasonal',
  'skipass~membership': 'skipass~membership',
  'skipass~return': 'skipass~return',
  'skipass~seasonal': 'skipass~seasonal',
  snowitcard: 'snowitcard',
  'snowitcard~bracelet': 'snowitcard~bracelet',
  transport: 'transport',
  'transport~ferry': 'transport~ferry',
  'transport~train': 'transport~train',
  'voucher~skipass': 'voucher~skipass',
};

const BIKE_LEVELS_ENUM: Dict<BikeLevelEnum> = {
  'beginner~ebike': 'beginner~ebike',
  'intermediate~ebike': 'intermediate~ebike',
  beginner: 'beginner',
  expert: 'expert',
  intermediate: 'intermediate',
};

const MISSING_STEP_OPTIONS_ENUM: Dict<MissingStepsOptionsEnum> = {
  optional: 'optional',
  required: 'required',
};

const REFUND_MODE_ENUM: Dict<ExtendedCancelationRefundEnum> = {
  'discount-code': 'discount-code',
  'refund-gateway': 'refund-gateway',
  offline: 'offline',
};

const POLICY_TYPE_ENUM: Dict<PolicyTypeEnum> = {
  cancelation: 'cancelation',
  modification: 'modification',
};

const ALL_GENDERS_ENUM: Dict<GenderEnum> = {
  F: 'F',
  M: 'M',
};

const EQUIPMENT_TYPES_ENUM: Dict<EquipmentTypeEnum> = {
  'e-bike': 'e-bike',
  'road-bike': 'road-bike',
  'trekking-bike': 'trekking-bike',
  other: 'other',
  ski: 'ski',
  snowboard: 'snowboard',
};

const EQUIPMENT_LEVELS_ENUM: Dict<EquipmentLevelEnum> = {
  economy: 'economy',
  smart: 'smart',
  top: 'top',
};

const LESSON_TYPES_ENUM: Dict<LessonTypeEnum> = {
  private: 'private',
  collective: 'collective',
};

const PRICE_RULE_ENUM: Dict<PriceRuleEnum> = {
  axess: 'axess',
  beebeeboard: 'beebeeboard',
  dayByDay: 'dayByDay',
  dynamic: 'dynamic',
  dynamicBundle: 'dynamicBundle',
  fixed: 'fixed',
  fixedPerDuration: 'fixedPerDuration',
  free: 'free',
  membership: 'membership',
  netstorming: 'netstorming',
  pricenow: 'pricenow',
  skidata: 'skidata',
  skiperformance: 'skiperformance',
  skiwork: 'skiwork',
  spotlio: 'spotlio',
  improoving: 'improoving',
  trenitalia: 'trenitalia',
  weakDynamicBundle: 'weakDynamicBundle',
  prize: 'prize',
};

const PAYMENT_GATEWAYS_ENUM: Dict<PaymentGatewayEnum> = {
  axepta: 'axepta',
  coupon: 'coupon',
  discount: 'discount',
  jointly: 'jointly',
  offline: 'offline',
  satispay: 'satispay',
  scalapay: 'scalapay',
  stripe: 'stripe',
  tantosvago: 'tantosvago',
  beneficy: 'beneficy',
};

const PAYMENT_TYPES_ENUM: Dict<PaymentTypeEnum> = {
  apple_pay: 'apple_pay',
  axepta: 'axepta',
  bank_transfer: 'bank_transfer',
  beneficy: 'beneficy',
  card: 'card',
  google_pay: 'google_pay',
  jointly: 'jointly',
  offline: 'offline',
  paypal_stripe: 'paypal_stripe',
  satispay: 'satispay',
  scalapay: 'scalapay',
  sepa_debit: 'sepa_debit',
  tantosvago: 'tantosvago',
};

const PAYMENT_BRANDS_ENUM: Dict<PaymentBrandEnum> = {
  amex: 'amex',
  diners: 'diners',
  discover: 'discover',
  jcb: 'jcb',
  maestro: 'maestro',
  mastercard: 'mastercard',
  other: 'other',
  unionpay: 'unionpay',
  visa: 'visa',
};

const CURRENCIES_ENUM: Dict<CurrencyEnum> = {
  CHF: 'CHF',
  EUR: 'EUR',
};

const DISCOUNT_TYPE_ENUM: Dict<DiscountTypeEnum> = {
  amount: 'amount',
  percentage: 'percentage',
};

const DISCOUNT_VARIETY_ENUM: Dict<DiscountVarietyEnum> = {
  referral: 'referral',
  esselunga: 'esselunga',
  giftcard: 'giftcard',
  itemCancellation: 'itemCancellation',
  orderCancellation: 'orderCancellation',
  other: 'other',
};

const PARTNER_TYPES_ENUM: Dict<PartnerTypeEnum> = {
  district: 'district',
  rental: 'rental',
  'rental~bike': 'rental~bike',
  customerCare: 'customerCare',
  experience: 'experience',
  food: 'food',
  parking: 'parking',
  other: 'other',
  lesson: 'lesson',
  'bundle~family-pack': 'bundle~family-pack',
  'bundle~dynamic': 'bundle~dynamic',
  'bundle~tab': 'bundle~tab',
  hotel: 'hotel',
  pickup: 'pickup',
  'bundle~train': 'bundle~train',
  'bundle~travel': 'bundle~travel',
  transport: 'transport',
};

const EXTERNAL_CODES_PROVIDERS_ENUM: Dict<ExternalCodeProviderNameEnum> = {
  comunemilano: 'comunemilano',
  evai: 'evai',
  gardaticket: 'gardaticket',
  midaticket: 'midaticket',
  'promo-cf-lombardia': 'promo-cf-lombardia',
  'promo-milano-cortina': 'promo-milano-cortina',
  'trenord-loyalty': 'trenord-loyalty',
  trenord: 'trenord',
  zucchetti: 'zucchetti',
};

const COUPON_TYPE_ENUM: Dict<CouponTypeEnum> = {
  SNOWITAPP: 'SNOWITAPP',
  EASYWELFARE: 'EASYWELFARE',
  FIERA: 'FIERA',
  BONDED: 'BONDED',
  GIFTCARD: 'GIFTCARD',
};

const CHECKOUT_CODES_STATUS_ENUM: Dict<CheckoutCodeStatusEnum> = {
  pending: 'pending',
  redeemed: 'redeemed',
};

const AGE_GROUP_ENUM: Dict<AgeGroupEnum> = {
  adult: 'adult',
  baby: 'baby',
  junior: 'junior',
  senior: 'senior',
  supersenior: 'supersenior',
  teen: 'teen',
};

const TAXONOMY_TYPE_ENUM: Dict<TaxonomyTypeEnum> = {
  bundle: 'bundle',
  'bundle~dynamic': 'bundle~dynamic',
  'bundle~family-pack': 'bundle~family-pack',
  'bundle~tab': 'bundle~tab',
  'bundle~train': 'bundle~train',
  'bundle~travel': 'bundle~travel',
  coupon: 'coupon',
  'experience~museum': 'experience~museum',
  experienceConcept: 'experienceConcept',
  food: 'food',
  hotel: 'hotel',
  itinerary: 'itinerary',
  lesson: 'lesson',
  parking: 'parking',
  rental: 'rental',
  'rental~bike': 'rental~bike',
  'rental~hotel': 'rental~hotel',
  seasonal: 'seasonal',
  service: 'service',
  skipass: 'skipass',
  'skipass~return': 'skipass~return',
  'skipass~seasonal': 'skipass~seasonal',
  transport: 'transport',
  'transport~ferry': 'transport~ferry',
};

const VENUE_TYPE_ENUM: Dict<VenueTypeEnum> = {
  entrance: 'entrance',
  other: 'other',
  pickup: 'pickup',
};

const PARTNER_BUSINESS_TYPE_ENUM: Dict<PartnerBusinessTypeEnum> = {
  company: 'company',
  individual: 'individual',
  non_profit: 'non_profit',
};

const BARCODE_MODES_ENUM: Dict<ProductBarcodeModeEnum> = {
  ble: 'ble',
  easyrent: 'easyrent',
  qrcode: 'qrcode',
  reserved: 'reserved',
  simple: 'simple',
  'simple-qrcode': 'simple-qrcode',
  skidata: 'skidata',
};
const PROFILE_TYPE_ENUM: Dict<ProfileTypeEnum> = {
  hotel: 'hotel',
  insurance: 'insurance',
  itinerary: 'itinerary',
  lesson: 'lesson',
  'lesson/bike': 'lesson/bike',
  'lesson/ski': 'lesson/ski',
  rent: 'rent',
  'rent/bike': 'rent/bike',
  'rent/ski': 'rent/ski',
  seasonal: 'seasonal',
  'seasonal/fisi': 'seasonal/fisi',
  'seasonal/maestri': 'seasonal/maestri',
  skipass: 'skipass',
  'skipass/promo-cf': 'skipass/promo-cf',
  'skipass/verified': 'skipass/verified',
  train: 'train',
  transport: 'transport',
  travel: 'travel',
};

const PROFILE_STATUS_ENUM: Dict<ProfileStatusEnum> = {
  'not-verified': 'not-verified',
  valid: 'valid',
  rejected: 'rejected',
  warning: 'warning',
};

const PROFILE_LEVELS_ENUM: Dict<ProfileLevelEnum> = {
  beginner: 'beginner',
  expert: 'expert',
  intermediate: 'intermediate',
};

const SHIPPING_PROVIDERS_ENUM: Dict<ShippingProviderEnum> = {
  shippyPro: 'shippyPro',
};

const SNOWITCARD_TYPES_ENUM: Dict<SnowitcardsTypesEnum> = {
  axess: 'axess',
  skidata: 'skidata',
  skinlombardia: 'skinlombardia',
  snowit: 'snowit',
};

const PROVIDER_STATUS_ENUM: Dict<ProviderOrderStatusEnum> = {
  blocked: 'blocked',
  cancelled: 'cancelled',
  cancelling: 'cancelling',
  delayed: 'delayed',
  failed: 'failed',
  needs_confirmation: 'needs_confirmation',
  ok: 'ok',
  pending_preorder: 'pending_preorder',
  pending: 'pending',
  waiting: 'waiting',
};
const SNOWITCARD_STATUS_ENUM: Dict<CardStatusEnum> = {
  valid: 'valid',
  membership: 'membership',
  pending: 'pending',
  warning: 'warning',
  rejected: 'rejected',
  'not-assigned': 'not-assigned',
};
const DEALER_STATUS_ENUM: Dict<DealerOrderStatusEnum> = {
  canceled: 'canceled',
  cash: 'cash',
  delivered: 'delivered',
  pending: 'pending',
};
const PROVIDER_SEMAPHORE_STATUS_ENUM: Dict<ProviderOrderStatusSemaphoreEnum> = {
  green: 'green',
  red: 'red',
  yellow: 'yellow',
};
const ORDER_STATUS_ENUM: Dict<OrderStatusEnum> = {
  ok: 'ok',
  fulfilled: 'fulfilled',
  'on-hold': 'on-hold',
  pending: 'pending',
  processing: 'processing',
  expired: 'expired',
  canceled: 'canceled',
};

const EMAIL_TYPES_ENUM: Record<TenantEmailTypeEnum, TenantEmailTypeEnum> = {
  'assign-snowit-card': 'assign-snowit-card',
  'barcode-list': 'barcode-list',
  'ble-ticket-list': 'ble-ticket-list',
  'edit-order': 'edit-order',
  'edit-order@cancel': 'edit-order@cancel',
  'edit-order@partner': 'edit-order@partner',
  'edit-order@partnercancel': 'edit-order@partnercancel',
  'external-codes': 'external-codes',
  'external-codes@trenord-loyalty': 'external-codes@trenord-loyalty',
  'new-coupon': 'new-coupon',
  'new-experience': 'new-experience',
  'new-form-hotel': 'new-form-hotel',
  'new-form-skipass-groups': 'new-form-skipass-groups',
  'new-giftcard': 'new-giftcard',
  'new-lesson': 'new-lesson',
  'new-membership-transit': 'new-membership-transit',
  // @ts-expect-error: email non ancora mergiata nel backend
  'new-membership-transit@predebit': 'new-membership-transit@predebit',
  'new-membership-transit@ppu-skip-block-card': 'new-membership-transit@ppu-skip-block-card',
  'new-membership': 'new-membership',
  'new-order': 'new-order',
  'new-order@confirmonhold': 'new-order@confirmonhold',
  'new-order@coupon': 'new-order@coupon',
  'new-order@onhold': 'new-order@onhold',
  'new-order@partner': 'new-order@partner',
  'new-pickup-post': 'new-pickup-post',
  'new-pickup': 'new-pickup',
  'new-rental': 'new-rental',
  'new-snowit-card': 'new-snowit-card',
  'new-travel': 'new-travel',
  'new-user': 'new-user',
  'on-hold': 'on-hold',
  'partner-recap-orders': 'partner-recap-orders',
  'ppu-payment-error': 'ppu-payment-error',
  'ppu-payment-error@predebit': 'ppu-payment-error@predebit',
  'ppu-payment-error@predebit-block': 'ppu-payment-error@predebit-block',
  'ppu-threshold': 'ppu-threshold',
  'ppu-threshold@district': 'ppu-threshold@district',
  'ppu-threshold@insurance': 'ppu-threshold@insurance',
  referral: 'referral',
  'refunded-order': 'refunded-order',
  'refunded-order@partial': 'refunded-order@partial',
  'reset-password': 'reset-password',
  'ppu-expiring-payment-reminder': 'ppu-expiring-payment-reminder',
  'ppu-expiring-payment-final-reminder': 'ppu-expiring-payment-final-reminder',
};

const BUNDLE_TRAVEL_STATUS_ENUM: Dict<BundleTravelStatusEnum> = {
  coming_soon: 'coming_soon',
  organized: 'organized',
  almost_confirmed: 'almost_confirmed',
  confirmed: 'confirmed',
  sold_out: 'sold_out',
  request_availability: 'request_availability',
  last_spots: 'last_spots',
};

const DEVICES_ENUM: Dict<DeviceEnum> = {
  app: 'app',
  web: 'web',
};

const USER_TYPE_ENUM: Dict<UserTypeEnum> = {
  USER: 'USER',
  PARTNER: 'PARTNER',
  ADMIN: 'ADMIN',
};

const USER_SUBTYPE_ENUM: Dict<UserSubTypeEnum> = {
  DISTRICT: 'DISTRICT',
  HOTEL: 'HOTEL',
  SUBADMIN: 'SUBADMIN',
};

const MARKUP_TYPES_ENUM: Dict<MarkupTypeEnum> = {
  amount: 'amount',
  percentage: 'percentage',
};

const TENANT_MENU_ENTRIES_ENUM: Dict<TenantMenuEntriesEnum> = {
  contacts: 'contacts',
  coupon: 'coupon',
  giftcards: 'giftcards',
  location: 'location',
  memberships: 'memberships',
  products: 'products',
  snowitcards: 'snowitcards',
  support: 'support',
  travel: 'travel',
};

const TENANT_MENUFOOTER_ENTRIES_ENUM: Dict<TenantFooterMenuEntriesEnum> = {
  categories: 'categories',
  company: 'company',
  products: 'products',
  support: 'support',
  custom: 'custom',
};

const TENANT_ANALYTICS_ENUM: Dict<TenantAnalyticsEnum> = {
  'tag-manager': 'tag-manager',
  facebook: 'facebook',
  sendinblue: 'sendinblue',
};

const TENANT_SCRIPTS_ENUM: Dict<TenantScriptsEnum> = {
  bugpilot: 'bugpilot',
  chatbase: 'chatbase',
  hotjar: 'hotjar',
  iubenda: 'iubenda',
  provedirect: 'provedirect',
  sendinblue: 'sendinblue',
  talkdesk: 'talkdesk',
  trustpilot: 'trustpilot',
};

const TENANT_LOGINS_ENUM: Dict<TenantLoginEnum> = {
  apple: 'apple',
  facebook: 'facebook',
  google: 'google',
  password: 'password',
  tantosvago: 'tantosvago',
};

const WIDGET_COMPONENTS_ENUM: Dict<TenantWidgetComponentEnum> = {
  calendar: 'calendar',
  category: 'category',
  duration: 'duration',
  guests: 'guests',
  lessonType: 'lessonType',
  location: 'location',
  partner: 'partner',
  periods: 'periods',
};

const WIDGET_VALIDATORS_ENUM: Dict<TenantWidgetValidatorEnum> = {
  bundle_dynamic: 'bundle_dynamic',
  hotel: 'hotel',
  hotel_details: 'hotel_details',
  itinerary: 'itinerary',
  itinerary_details: 'itinerary_details',
  lesson: 'lesson',
  rental: 'rental',
  zone: 'zone',
  zone_date: 'zone_date',
};

const WIDGET_FILTERS_ENUM: Dict<TenantWidgetFilterEnum> = {
  category: 'category',
  equipmentType: 'equipmentType',
  gear: 'gear',
  lessonType: 'lessonType',
  price: 'price',
  services: 'services',
  skipass_duration: 'skipass_duration',
  sorting_hotel: 'sorting_hotel',
  stars: 'stars',
  tag: 'tag',
};

const ZONES_SERVICES_ENUM: Dict<ZoneServiceEnum> = {
  babyPark: 'babyPark',
  crossCountry: 'crossCountry',
  disco: 'disco',
  iceCart: 'iceCart',
  iceSkating: 'iceSkating',
  paragliding: 'paragliding',
  shopping: 'shopping',
  starredRestaurant: 'starredRestaurant',
  tobogganTrack: 'tobogganTrack',
  wellness: 'wellness',
  worldCup: 'worldCup',
};

const MEMBERSHIP_INTERVALS_ENUM: Dict<MembershipIntervalEnum> = {
  day: 'day',
  month: 'month',
  season: 'season',
  week: 'week',
  year: 'year',
};

const MEMBERSHIP_STATUS_ENUM: Dict<MembershipStatusEnum> = {
  active: 'active',
  canceled: 'canceled',
  incomplete: 'incomplete',
  incomplete_expired: 'incomplete_expired',
  past_due: 'past_due',
  paused: 'paused',
  pending: 'pending',
  trialing: 'trialing',
  unpaid: 'unpaid',
};

const MEMBERSHIP_NON_CANCELED_STATUS_ENUM: Dict<Exclude<MembershipStatusEnum, 'canceled'>> = {
  active: 'active',
  incomplete: 'incomplete',
  incomplete_expired: 'incomplete_expired',
  past_due: 'past_due',
  paused: 'paused',
  pending: 'pending',
  trialing: 'trialing',
  unpaid: 'unpaid',
};

const EXTERNAL_CODES_STATUS_ENUM: Dict<ExternalCodeStatusEnum> = {
  canceled: 'canceled',
  created: 'created',
  sent: 'sent',
  used: 'used',
};

const EXTERNAL_CODES_VALUE_TYPE_ENUM: Dict<ExternalCodeValueTypeEnum> = {
  amount: 'amount',
  percentage: 'percentage',
};

const FORM_STATUS_ENUM: Dict<FormStatusEnum> = {
  redeemed: 'redeemed',
  sent: 'sent',
  pending: 'pending',
  disabled: 'disabled',
  error: 'error',
};

const ZONE_DIRECTION_TYPES_ENUM: Dict<ZoneDirectionTypeEnum> = {
  airplane: 'airplane',
  bike: 'bike',
  bus: 'bus',
  car: 'car',
  ferry: 'ferry',
  train: 'train',
};

const MEMBERSHIP_TYPE_ENUM: Dict<MembershipTypeEnum> = {
  ppu: 'ppu',
  seasonal: 'seasonal',
};

const MEMBERSHIP_CARD_STATUS_ENUM: Dict<MembershipCardAssignmentStatusEnum> = {
  assigned: 'assigned',
  blocked: 'blocked',
  error: 'error',
  pending: 'pending',
};

// const ZONE_TEXTBLOCK_TYPES_ENUM: Dict<ZoneTextBlockTypeEnum> = {
//   bulletpoints: 'bulletpoints',
//   default: 'default',
// };

const VALID_LOCALES_ENUM: Dict<ValidLocaleEnum> = {
  de: 'de',
  en: 'en',
  fr: 'fr',
  it: 'it',
};

const ZONE_PPU_CLASSIFICATIONS_ENUM: Dict<ZonePPUClassificationEnum> = {
  gold: 'gold',
  silver: 'silver',
  bronze: 'bronze',
};

const API_KEY_SERVICES_ENUM: Dict<ApiKeyServices> = {
  graphql: 'graphql',
  sso: 'sso',
};

const SOCIAL_GALLERY_VIDEO_TYPE_ENUM: Dict<VideoTypeEnum> = {
  vimeo: 'vimeo',
  youtube: 'youtube',
};

const SOCIAL_GALLERY_SOCIAL_POST_TYPE_ENUM: Dict<SocialPostTypeEnum> = {
  tiktok: 'tiktok',
  instagram_post: 'instagram_post',
  instagram_reel: 'instagram_reel',
};

const WALLET_SEASON_STATUS_ENUM: Dict<WalletSeasonStatusEnum> = {
  active: 'active',
  expired: 'expired',
};

// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// --- exports
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---
// ---

export const AGE_GROUP = Object.values(AGE_GROUP_ENUM);
export const AGE_GROUP_ADULTS = [
  'adult',
  'senior',
  'supersenior',
] as const satisfies ReadonlyArray<AgeGroupEnum>;

export const AGE_GROUP_BABIES = [
  'baby',
  'teen',
  'junior',
] as const satisfies ReadonlyArray<AgeGroupEnum>;

export const BARCODE_MODES = Object.values(BARCODE_MODES_ENUM);
export const PROFILE_TYPE = Object.values(PROFILE_TYPE_ENUM);
export const PROFILE_STATUS = Object.values(PROFILE_STATUS_ENUM);
export const BIKE_LEVELS = Object.values(BIKE_LEVELS_ENUM);
export const PROFILE_LEVELS = Object.values(PROFILE_LEVELS_ENUM);
export const ALL_GENDERS = Object.values(ALL_GENDERS_ENUM);
export const COUPON_TYPE = Object.values(COUPON_TYPE_ENUM);
export const CURRENCIES = Object.values(CURRENCIES_ENUM);
export const CURRENCIES_SYMBOLS: Record<CurrencyEnum, string> = {
  EUR: '€',
  CHF: '₣',
};
export const DISCOUNT_TYPE = Object.values(DISCOUNT_TYPE_ENUM);
export const DISCOUNT_VARIETY = Object.values(DISCOUNT_VARIETY_ENUM);
export const DISCOUNT_CUSTOM_VARIETY = [
  'collab_B2B',
  'collab_epipoli',
  'collab_esselunga',
  'collab_mediaword',
  'collab_others',
  'collab_tenants',
  'collab_welfare',
  'collab_beneficy',
  'customer_emergency',
  'customer_offline',
  'customer_refund_parziale',
  'customer_sorry',
  'family_friends',
  'giftcard',
  'itemCancellation',
  'mktg_campaign',
  'mktg_collab',
  'mktg_sponsor',
  'mktg_user',
  'orderCancellation',
  'other',
  'referral',
  'regenerated',
  'test',
] as const;
export const DISCOUNT_REDEEM_MODE = ['single', 'multi'] as const;
export const EQUIPMENT_LEVELS = Object.values(EQUIPMENT_LEVELS_ENUM);
export const EQUIPMENT_TYPES = Object.values(EQUIPMENT_TYPES_ENUM);
export const EQUIPMENT_TYPES_ITINERARY = [
  'e-bike',
  'road-bike',
  'trekking-bike',
] as const satisfies ReadonlyArray<EquipmentTypeEnum>;
export const EXTERNAL_CODES_PROVIDERS = Object.values(EXTERNAL_CODES_PROVIDERS_ENUM);
export const EXTERNAL_CODES_STATUS = Object.values(EXTERNAL_CODES_STATUS_ENUM);
export const EXTERNAL_CODES_VALUE_TYPE = Object.values(EXTERNAL_CODES_VALUE_TYPE_ENUM);
export const LESSON_TYPES = Object.values(LESSON_TYPES_ENUM);
export const PARTNER_BUSINESS_TYPE = Object.values(PARTNER_BUSINESS_TYPE_ENUM);
export const PARTNER_MODAL_INFOS = ['parking', 'phoneNumber', 'POI'] as const;
export const PARTNER_TYPES = Object.values(PARTNER_TYPES_ENUM);
export const PAYMENT_GATEWAYS = Object.values(PAYMENT_GATEWAYS_ENUM);
export const PAYMENT_TYPES = Object.values(PAYMENT_TYPES_ENUM);
export const PAYMENT_BRANDS = Object.values(PAYMENT_BRANDS_ENUM);
export const PAYMENT_TYPES_STRIPE = [
  'card',
  'sepa_debit',
  'paypal_stripe',
  'google_pay',
  'apple_pay',
  'bank_transfer',
] as const satisfies ReadonlyArray<PaymentTypeEnum>;
export const POLICY_TYPE = Object.values(POLICY_TYPE_ENUM);
export const PRICE_RULE = Object.values(PRICE_RULE_ENUM);
export const PRICE_RULE_CREATE = [
  'dayByDay',
  'dynamic',
  'fixed',
  'fixedPerDuration',
  'membership',
  'prize',
] as const satisfies ReadonlyArray<PriceRuleEnum>;
export const PRODUCT_TYPES = Object.values(PRODUCT_TYPES_ENUM);
export const REFUND_MODE = Object.values(REFUND_MODE_ENUM);
export const TAXONOMY_TYPE = Object.values(TAXONOMY_TYPE_ENUM);
export const VENUE_TYPE = Object.values(VENUE_TYPE_ENUM);
export const BUNDLE_DYNAMICS = [
  'bundle~dynamic',
  'bundle~train',
  'bundle~travel',
] as const satisfies ReadonlyArray<ProductTypeEnum>;
export const SHIPPING_PROVIDERS = Object.values(SHIPPING_PROVIDERS_ENUM);
export const SNOWITCARD_TYPES = Object.values(SNOWITCARD_TYPES_ENUM);
export const PROVIDER_STATUS = Object.values(PROVIDER_STATUS_ENUM);
export const SNOWITCARD_STATUS = Object.values(SNOWITCARD_STATUS_ENUM);
export const SNOWITCARD_STATUS_PARTNERS: CardStatusEnum[] = Object.values(
  SNOWITCARD_STATUS_ENUM,
).filter((it) => it !== 'not-assigned');
export const DEALER_STATUS = Object.values(DEALER_STATUS_ENUM);
export const PROVIDER_SEMAPHORE_STATUS = Object.values(PROVIDER_SEMAPHORE_STATUS_ENUM);
export const ORDER_STATUS = Object.values(ORDER_STATUS_ENUM);
export const EMAIL_TYPES = Object.values(EMAIL_TYPES_ENUM).sort();
export const BUNDLE_TRAVEL_STATUS = Object.values(BUNDLE_TRAVEL_STATUS_ENUM);
export const CANCELABLE_MEMBERSHIP_STATUS: MembershipStatusEnum[] = [
  'pending',
  'active',
  'paused',
  'trialing',
];

export const DEALER_STATUS_COUNT = [
  'canceled',
  'cash',
  'delivered',
  'pending',
] as const satisfies ReadonlyArray<DealerOrderStatusEnum>;

export const SNOWITCARD_STATUS_COUNT = [
  'pending',
  'warning',
  'valid',
  'rejected',
] as const satisfies ReadonlyArray<CardStatusEnum>;

export const PROVIDER_STATUS_COUNT: ProviderOrderStatusEnum[] = [
  'cancelled',
  'cancelling',
  'failed',
  'ok',
  'pending',
  'waiting',
  'delayed',
  'pending_preorder',
  'needs_confirmation',
];

export const PROVIDER_SEMAPHORE_STATUS_COUNT: ProviderOrderStatusSemaphoreEnum[] = ['red'];

export const DEVICES = Object.values(DEVICES_ENUM);

export const USER_TYPES = Object.values(USER_TYPE_ENUM);

export const USER_SUBTYPES = [
  USER_SUBTYPE_ENUM.DISTRICT,
  USER_SUBTYPE_ENUM.HOTEL,
] as const satisfies ReadonlyArray<UserSubTypeEnum>;

export const MISSING_STEP_OPTIONS = Object.values(MISSING_STEP_OPTIONS_ENUM);

export const MARKUP_TYPES = Object.values(MARKUP_TYPES_ENUM);

export const EXTERNAL_PROVIDER_PARAMS_KEYS = Object.values(EXTERNAL_PROVIDER_PARAMS_KEYS_ENUM);

export const TENANT_MENU_ENTRIES = Object.values(TENANT_MENU_ENTRIES_ENUM);
export const TENANT_MENUFOOTER_ENTRIES = Object.values(TENANT_MENUFOOTER_ENTRIES_ENUM);

export const TENANT_ANALYTICS = Object.values(TENANT_ANALYTICS_ENUM);
export const TENANT_SCRIPTS = Object.values(TENANT_SCRIPTS_ENUM);
export const TENANT_LOGINS = Object.values(TENANT_LOGINS_ENUM);

export const WIDGET_COMPONENTS = Object.values(WIDGET_COMPONENTS_ENUM);
export const WIDGET_VALIDATORS = Object.values(WIDGET_VALIDATORS_ENUM);
export const WIDGET_FILTERS = Object.values(WIDGET_FILTERS_ENUM);
export const ZONES_SERVICES = Object.values(ZONES_SERVICES_ENUM);
export const MEMBERSHIP_INTERVALS = Object.values(MEMBERSHIP_INTERVALS_ENUM);
export const MEMBERSHIP_STATUS = Object.values(MEMBERSHIP_STATUS_ENUM);
export const MEMBERSHIP_NON_CANCELED_STATUS = Object.values(MEMBERSHIP_NON_CANCELED_STATUS_ENUM);
export const FORM_STATUS = Object.values(FORM_STATUS_ENUM);
export const ZONE_DIRECTION_TYPES = Object.values(ZONE_DIRECTION_TYPES_ENUM);
// export const ZONE_TEXTBLOCK_TYPES = Object.values(ZONE_TEXTBLOCK_TYPES_ENUM);
export const ZONE_TEXTBLOCK_TYPES: ZoneTextBlockTypeEnum[] = ['default', 'ppu_classification'];
export const PPU_PROVIDERS = Object.values(PPU_PROVIDERS_ENUM);
export const MEMBERSHIP_TYPE = Object.values(MEMBERSHIP_TYPE_ENUM);
export const MEMBERSHIP_CARD_STATUS = Object.values(MEMBERSHIP_CARD_STATUS_ENUM);
export const VALID_LOCALES = Object.values(VALID_LOCALES_ENUM);
export const ZONE_PPU_CLASSIFICATIONS = Object.values(ZONE_PPU_CLASSIFICATIONS_ENUM);
export const API_KEY_SERVICES = Object.values(API_KEY_SERVICES_ENUM);
export const CHALLENGE_GOAL_TYPES = Object.values(CHALLENGE_GOAL_TYPE_ENUM);
export const TURNSTILE_TYPES = Object.values(TURNSTILE_TYPE_ENUM);
export const TURNSTILE_DIRECTIONS = Object.values(TURNSTILE_DIRECTION_ENUM);
export const CARD_DEVICE_TYPES = Object.values(CARD_DEVICE_ENUM);
export const TRANSIT_STATUSES = Object.values(TRANSIT_STATUS_ENUM);
export const CHECKOUT_CODES_STATUS = Object.values(CHECKOUT_CODES_STATUS_ENUM);
export const SOCIAL_GALLERY_VIDEO_TYPE = Object.values(SOCIAL_GALLERY_VIDEO_TYPE_ENUM);
export const SOCIAL_GALLERY_SOCIAL_POST_TYPE = Object.values(SOCIAL_GALLERY_SOCIAL_POST_TYPE_ENUM);
export const WALLET_SEASON_STATUS = Object.values(WALLET_SEASON_STATUS_ENUM);
